import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import * as XLSX from 'xlsx';
import ApiService from '../models/ApiService';


const ImportVocabulary = () => {
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [previewData, setPreviewData] = useState([]);
  const [columns, setColumns] = useState([]);
  const [validationError, setValidationError] = useState('');

  const validateStructure = (headers) => {
    const requiredColumns = ['category', 'word', 'transliteration', 'meaning', 'english_name', 'devanagari_transliteration', 'hindi_meaning'];
    return JSON.stringify(headers) === JSON.stringify(requiredColumns);
  };


  const onDrop = useCallback((acceptedFiles) => {
    setUploadError(null);
    setSuccessMessage('');
    setUploadProgress(0);
    setValidationError('');

    if (acceptedFiles.length === 0) return;

    const selectedFile = acceptedFiles[0];

    // Check file size (max 5MB)
    if (selectedFile.size > 5 * 1024 * 1024) {
      setValidationError('File size should not exceed 5MB');
      return;
    }

    setFile(selectedFile);
    setFileName(selectedFile.name);

    // Read and preview the Excel file
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(firstSheet, { header: 1 });

        if (jsonData.length === 0) {
          setValidationError('The Excel file is empty');
          return;
        }

        const headers = jsonData[0].map(header => header.toLowerCase().trim());

        // Validate the structure
        if (!validateStructure(headers)) {
          setValidationError('Invalid Excel structure. Required columns: category,  word, transliteration, meaning, english_name, devanagari_transliteration , hindi_meaning');
          setFile(null);
          setFileName('');
          return;
        }

        setColumns(headers);
        setPreviewData(jsonData.slice(1, 10)); // Show first 10 rows for preview
      } catch (error) {
        console.error('Error reading file:', error);
        setValidationError('Error reading the Excel file. Please check the format.');
        setFile(null);
        setFileName('');
      }
    };
    reader.readAsArrayBuffer(selectedFile);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
    },
    maxFiles: 1,
  });

  const handleUpload = async () => {
    if (!file) return;

    setIsUploading(true);
    setUploadError(null);
    setSuccessMessage('');

    try {
      const formData = new FormData();
      formData.append('file', file);
      const config = {
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setUploadProgress(percentCompleted);
        },
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      };

      const res = await ApiService.ImportVocabulary(formData, config);
      console.log(res.data.responseContent);
      setSuccessMessage(res.data.message || 'Vocabulary imported successfully!');
      handleCancel();
    } catch (error) {
      console.error('Upload error:', error);
      setUploadError(
        error.response?.data?.message ||
        error.message ||
        'Failed to upload file. Please try again.'
      );
    } finally {
      setIsUploading(false);
    }


  };

  const handleCancel = () => {
    setFile(null);
    setFileName('');
    setUploadProgress(0);
    setPreviewData([]);
    setColumns([]);
    setUploadError(null);
    setSuccessMessage('');
    setValidationError('');
  };

  return (
    <div className="w-full p-6 bg-white rounded-lg shadow-md" id='primary-fonts'>
      <h2 className="text-2xl font-bold text-[--text-color] mb-6">Import Vocabulary from Excel</h2>

      <div className="mb-6">
        <div
          {...getRootProps()}
          className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${isDragActive ? 'border-[--border-color-g] bg-blue-50' : 'border-gray-300 hover:border-[--border-color-o]'
            }`}
        >
          <input {...getInputProps()} />
          <div className="flex flex-col items-center justify-center space-y-2">
            <svg
              className="w-12 h-12 text-[--text-color]"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
              />
            </svg>
            <p className="text-gray-600">
              {isDragActive
                ? 'Drop the Excel file here'
                : 'Drag & drop an Excel file here, or click to select'}
            </p>
            <p className="text-sm text-gray-500">Supports .xls, .xlsx files (max 5MB)</p>
          </div>
        </div>
      </div>

      {validationError && (
        <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-md">
          {validationError}
        </div>
      )}

      {fileName && (
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-black">Selected File: <span className="text-[--text-color]">{fileName}</span></p>
            <button
              onClick={handleCancel}
              className="text-red-500 hover:text-red-700 border border-red-600 p-1 rounded-md text-sm font-medium"
            >
              Remove
            </button>
          </div>

          {uploadProgress > 0 && uploadProgress < 100 && (
            <div className="w-full bg-gray-200 rounded-full h-2.5">
              <div
                className="bg-blue-600 h-2.5 rounded-full"
                style={{ width: `${uploadProgress}%` }}
              ></div>
            </div>
          )}
        </div>
      )}

      {previewData.length > 0 && (
        <div className="mb-6">
          <h3 className="font-medium text-gray-700 mb-2">Preview </h3>
          <div className="overflow-x-auto border rounded-lg">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {columns.map((col, index) => (
                    <th
                      key={index}
                      scope="col"
                      className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {previewData.map((row, rowIndex) => (
                  <tr key={rowIndex}>
                    {columns.map((col, colIndex) => (
                      <td
                        key={colIndex}
                        className="px-4 py-2 whitespace-nowrap text-sm text-gray-500"
                      >
                        {row[colIndex] || '-'}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {file && !isUploading && !validationError && (
        <button
          onClick={handleUpload}
          disabled={isUploading}
          className="w-full bg-[--primary-btn-color] hover:bg-[--secondary-btn-color] text-white font-medium py-2 px-4 rounded-md transition-colors"
        >
          Upload Vocabulary
        </button>
      )}

      {isUploading && (
        <button
          disabled
          className="w-full bg-[--secondary-btn-color] text-white font-medium py-2 px-4 rounded-md cursor-not-allowed"
        >
          Uploading... {uploadProgress}%
        </button>
      )}

      {uploadError && (
        <div className="mt-4 p-3 bg-red-50 text-red-700 rounded-md">
          {uploadError}
        </div>
      )}

      {successMessage && (
        <div className="mt-4 p-3 bg-green-50 text-green-700 rounded-md">
          {successMessage}
        </div>
      )}

      <div className="mt-6 text-sm text-gray-500">
        <h4 className="font-medium mb-2">Excel File Requirements:</h4>
        <ul className="list-disc pl-5 space-y-1">
          <li>First row must contain these exact column headers: <strong>category, word, transliteration, meaning, english_name, devanagari_transliteration, hindi_meaning
          </strong></li>
          <li>Each row should contain a complete vocabulary entry with all fields</li>
          <li>File size should not exceed 5MB</li>
          <li>Example structure:
            <div className="overflow-x-auto w-full mt-2">
              <table className="min-w-full border-collapse border border-gray-300">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="p-2 border border-gray-300 text-left">Category</th>
                    <th className="p-2 border border-gray-300 text-left">Word</th>
                    <th className="p-2 border border-gray-300 text-left">Transliteration</th>
                    <th className="p-2 border border-gray-300 text-left">Meaning</th>
                    <th className="p-2 border border-gray-300 text-left">English Name</th>
                    <th className="p-2 border border-gray-300 text-left">Devanagri transliteration</th>
                    <th className="p-2 border border-gray-300 text-left">Hindi Meaning</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="p-2 border border-gray-300">Daily Use Terms</td>
                    <td className="p-2 border border-gray-300">اَدَب</td>
                    <td className="p-2 border border-gray-300">Adab</td>
                    <td className="p-2 border border-gray-300">Respect; also literature</td>
                    <td className="p-2 border border-gray-300">english name</td>
                    <td className="p-2 border border-gray-300">अदब</td>
                    <td className="p-2 border border-gray-300">hindi meaning</td>
                  </tr>
                </tbody>
              </table>
            </div>

          </li>
        </ul>
      </div>
    </div>
  );
};

export default ImportVocabulary;

